/* global videojs */

const createCustomComponents = (bcInstance) => {
    const Component = videojs.getComponent('Component');
    const chaptersContainer = document.querySelector('.js_chaptersContainer');
    const playbackRateMenuButton = bcInstance._player.getDescendant(['ControlBar', 'PlaybackRateMenuButton']);
    const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;
    const currentPlaybackRate = bcInstance._loadUserPlaybackRate();

    let customControlsWrap;
    let playbackRateControlsWrap;
    let seekLabel;
    let playPauseButton;
    let seekForwardButton;
    let seekBackwardButton;
    let tapToUnmuteButton;

    const playPauseVideo = () => {
        if (bcInstance._player.paused()) {
            bcInstance._player.play();
        } else {
            bcInstance._player.pause();
        }
    };

    const setTapToUnmute = () => {
        if (!bcInstance._player.muted()) {
            return;
        }

        tapToUnmuteButton.classList.add('is-visible');
    };

    const trackTapToUnmute = (source) => {
        const eventTimestampMiliseconds = Math.floor(bcInstance._currentTime * 1000);
        window.eventTracker.trackTapToUnmute(source, eventTimestampMiliseconds);
        gtag('event', 'player_unmuted', {
            event_category: 'Video',
            event_label: context?.videoId,
            video_timestamp: eventTimestampMiliseconds,
        });
        window.vumedi.mixpanel?.track('PlayerUnmuted', {
            viewed_uuid: context.viewedUUID,
            video_timestamp: eventTimestampMiliseconds,
            template_version: context.templateVersion,
            surface: this._surface,
        });
    };

    /**
     * Handle 'Tap to unmute' player event by hiding the button and tracking an event.
     *
     * @param {'tap-to-unmute-button'|'volume-control'} source
     */
    const handleTapToUnmute = (source) => {
        tapToUnmuteButton.classList.remove('is-visible');
        bcInstance._player.muted(false);

        if (bcInstance._player.volume() === 0) {
            bcInstance._player.volume(1);
        }

        trackTapToUnmute(source);
    };

    const customControlsWrapClickHandler = (e) => {
        // if we click on the custom controls wrap element on desktop, we should toggle play/pause
        const clickTargetIsCorrect = e.target.classList.contains('js_videoInnerWrap');
        if (bcInstance._isTouchDevice || !clickTargetIsCorrect) return;
        playPauseVideo();
    };

    const customControlsWrapTouchEndHandler = (e) => {
        const clickTargetIsCorrect = e.target.classList.contains('js_videoInnerWrap');
        const controlsAreShown = customControlsWrap.classList.contains('show-seek-controls');

        if (bcInstance._isTouchDevice && clickTargetIsCorrect) {
            if (controlsAreShown) {
                customControlsWrap.classList.remove('show-seek-controls');
                customControlsWrap.classList.add('disable-hover-styles');

                setTimeout(() => {
                    bcInstance._player.userActive(false);
                }, 1);
            } else {
                customControlsWrap.classList.add('show-seek-controls');
                bcInstance._startControlsTimeout('touchend', customControlsWrap);
            }
        }
    };

    // hide controls when someone clicks outside of the video
    document.addEventListener('click', (e) => {
        const targetElement = e.target;
        const isInsideCustomControls = targetElement.closest('.js_videoInnerWrap');
        const isInsideControlBar = targetElement.closest('.vjs-control-bar');

        if (!isInsideCustomControls && !isInsideControlBar) {
            customControlsWrap?.classList.remove('show-seek-controls');
            bcInstance._player.userActive(false);
            seekLabel?.classList.remove('is-visible');
        }
    });

    class CustomSeekAndPlayControls extends Component {
        createEl() {
            customControlsWrap = videojs.dom.createEl('div', {
                className: 'vjs-custom-controls-wrap js_videoInnerWrap',
            });

            customControlsWrap.addEventListener('click', (e) => customControlsWrapClickHandler(e));
            customControlsWrap.addEventListener('touchend', (e) => customControlsWrapTouchEndHandler(e));

            seekLabel = videojs.dom.createEl('div', {
                className: 'vjs-seek-label js_videoSeekLabel',
                innerHTML: `
                    <i class="icon icon-seek"></i>
                    <p>10 seconds</p>
                `,
            });

            const customControls = videojs.dom.createEl('div', {
                className: 'vjs-custom-controls js_videoCustomControls',
            });

            // seek backward button
            seekBackwardButton = videojs.dom.createEl('button', {
                className: 'vjs-custom-control-button vjs-seek-backward-button js_videoSeekBackward',
                controlText: 'Seek backward button',
                innerHTML: '<i class="icon icon-seek-backward-overlay"></i>',
            });

            seekBackwardButton?.addEventListener('click', () => {
                if (!bcInstance._player.userActive()) {
                    return;
                }

                bcInstance._seekVideo('backward', 'button');
                bcInstance._startControlsTimeout('click', customControlsWrap);
            });

            // play/pause button
            const isPaused = bcInstance._player.paused();
            playPauseButton = videojs.dom.createEl('button', {
                className: `vjs-custom-control-button vjs-play-pause-button js_videoPlayPause${isPaused ? ' is-paused' : ''}`,
                controlText: 'Play/Pause button',
                innerHTML: `
                    <i class="icon icon-play-overlay"></i>
                    <i class="icon icon-pause-overlay"></i>
                `,
            });

            playPauseButton.addEventListener('click', () => {
                if (!bcInstance._player.userActive()) {
                    return;
                }

                playPauseVideo();
            });

            // seek forward button
            seekForwardButton = videojs.dom.createEl('button', {
                className: 'vjs-custom-control-button vjs-seek-forward-button js_videoSeekForward',
                controlText: 'Seek forward button',
                innerHTML: '<i class="icon icon-seek-forward-overlay"></i>',
            });

            seekForwardButton?.addEventListener('click', () => {
                if (!bcInstance._player.userActive()) {
                    return;
                }

                bcInstance._seekVideo('forward', 'button');
                bcInstance._startControlsTimeout('click', customControlsWrap);
            });

            // Append the buttons to the wrapper div
            customControls.appendChild(seekBackwardButton);
            customControls.appendChild(playPauseButton);
            customControls.appendChild(seekForwardButton);
            customControlsWrap.appendChild(seekLabel);
            customControlsWrap.appendChild(customControls);

            return customControlsWrap;
        }
    }

    class CustomControlBarSeekButtons extends Component {
        createEl() {
            const controlBar = bcInstance._player.getChild('ControlBar');
            const volumePanel = bcInstance._player.getDescendant(['ControlBar', 'VolumePanel']);

            const seekButtonsWrap = videojs.dom.createEl('div', {
                className: 'vjs-control-bar-seek-buttons',
            });

            const controlBarSeekBackwardButton = videojs.dom.createEl('button', {
                className:
                    'vjs-control vjs-button vjs-control-bar-seek-button vjs-seek-backward-button js_videoSeekBackward',
                controlText: 'Seek backward button',
                innerHTML: '<i class="icon icon-seek-backward"></i>',
            });

            controlBarSeekBackwardButton.addEventListener('click', () => {
                bcInstance._seekVideo('backward', 'button');
            });

            const controlBarSeekForwardButton = videojs.dom.createEl('button', {
                className:
                    'vjs-control vjs-button vjs-control-bar-seek-button vjs-seek-forward-button js_videoSeekForward',
                controlText: 'Seek forward button',
                innerHTML: '<i class="icon icon-seek-forward"></i>',
            });

            controlBarSeekForwardButton.addEventListener('click', () => {
                bcInstance._seekVideo('forward', 'button');
            });

            seekButtonsWrap.appendChild(controlBarSeekBackwardButton);
            seekButtonsWrap.appendChild(controlBarSeekForwardButton);

            // Insert before the volume panel (mute button)
            if (volumePanel && volumePanel.el_) {
                volumePanel.el_.before(seekButtonsWrap);
            } else {
                controlBar.el_.appendChild(seekButtonsWrap);
            }
        }
    }

    class CustomPlaybackRateControls extends Component {
        createEl() {
            const playbackRates = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

            const playbackRateControlsContainer = videojs.dom.createEl('div', {
                className: 'vjs-custom-playback-rate-container',
                innerHTML: '<div class="vjs-custom-playback-rate-screen-overlay"></div>',
            });

            playbackRateControlsWrap = videojs.dom.createEl('div', {
                className: 'vjs-custom-playback-rate-wrap js_playRateOptionsWrap',
            });

            const closeButton = videojs.dom.createEl('button', {
                className: 'vjs-playback-rate-close js_playRateCloseButton',
                controlText: 'Hide playback rate options',
                innerHTML: '<i class="icon icon-x" />',
            });

            closeButton.addEventListener('click', () => {
                playbackRateControlsWrap.classList.remove('is-expanded');
            });

            const controlsWrapHeader = videojs.dom.createEl('div', {
                className: 'vjs-playback-rate-header',
                innerHTML: '<p>Playback speed</p>',
            });

            const playbackRateOptions = videojs.dom.createEl('div', {
                className: 'vjs-playback-rate-options',
            });

            playbackRates.forEach((rate) => {
                const buttonClasses = `
                    vjs-playback-rate-option-button js_playRateOption ${rate === currentPlaybackRate ? 'is-active' : ''}
                `.trim();
                const playbackRateOptionButton = videojs.dom.createEl('button', {
                    className: buttonClasses,
                    controlText: `Set playback speed to ${rate}x`,
                    innerHTML: `${rate}x ${rate === 1 ? '(Normal)' : ''}`,
                });
                playbackRateOptionButton.dataset.playRate = rate;
                playbackRateOptionButton.addEventListener('click', () => {
                    const currentActiveItem = playbackRateControlsWrap?.querySelector('.js_playRateOption.is-active');
                    currentActiveItem?.classList.remove('is-active');
                    playbackRateOptionButton?.classList.add('is-active');

                    bcInstance._player.playbackRate(rate);
                    playbackRateControlsWrap.classList.remove('is-expanded');
                });
                playbackRateOptions.appendChild(playbackRateOptionButton);
            });

            controlsWrapHeader.appendChild(closeButton);
            playbackRateControlsWrap.appendChild(controlsWrapHeader);
            playbackRateControlsWrap.appendChild(playbackRateOptions);
            playbackRateControlsContainer.prepend(playbackRateControlsWrap);

            return playbackRateControlsContainer;
        }
    }

    class CustomVideoTimestampAndChapterTitle extends Component {
        createEl() {
            const metadataWrap = videojs.dom.createEl('div', {
                className: 'vjs-custom-metadata-container',
            });
            let chapterTitle;

            const controlBar = bcInstance._player.getChild('ControlBar');
            const currentTimeDisplay = bcInstance._player.getDescendant(['ControlBar', 'CurrentTimeDisplay']);
            const timeDivider = bcInstance._player.getDescendant(['ControlBar', 'TimeDivider']);
            const durationTimeDisplay = bcInstance._player.getDescendant(['ControlBar', 'DurationDisplay']);

            metadataWrap.appendChild(currentTimeDisplay.el_);
            metadataWrap.appendChild(timeDivider.el_);
            metadataWrap.appendChild(durationTimeDisplay.el_);

            if (chaptersContainer) {
                chapterTitle = videojs.dom.createEl('p', {
                    className: 'vjs-custom-metadata-chapter-title',
                    innerHTML: '<span></span>',
                });

                metadataWrap.appendChild(chapterTitle);
            }

            controlBar.el_.prepend(metadataWrap);
        }
    }

    class CustomTapToUnmuteButton extends Component {
        createEl() {
            tapToUnmuteButton = videojs.dom.createEl('button', {
                className: 'vjs-custom-control-button vjs-custom-control-button--tap-to-unmute',
                innerHTML: `
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 8.99993L16 14.9999M16 8.99993L22 14.9999M9.63432 5.36561L6.46863 8.5313C6.29568 8.70425 6.2092 8.79073 6.10828 8.85257C6.01881 8.9074 5.92127 8.9478 5.81923 8.9723C5.70414 8.99993 5.58185 8.99993 5.33726 8.99993H3.6C3.03995 8.99993 2.75992 8.99993 2.54601 9.10892C2.35785 9.20479 2.20487 9.35777 2.10899 9.54594C2 9.75985 2 10.0399 2 10.5999V13.3999C2 13.96 2 14.24 2.10899 14.4539C2.20487 14.6421 2.35785 14.7951 2.54601 14.8909C2.75992 14.9999 3.03995 14.9999 3.6 14.9999H5.33726C5.58185 14.9999 5.70414 14.9999 5.81923 15.0276C5.92127 15.0521 6.01881 15.0925 6.10828 15.1473C6.2092 15.2091 6.29568 15.2956 6.46863 15.4686L9.63431 18.6342C10.0627 19.0626 10.2769 19.2768 10.4608 19.2913C10.6203 19.3038 10.7763 19.2392 10.8802 19.1175C11 18.9773 11 18.6744 11 18.0686V5.9313C11 5.32548 11 5.02257 10.8802 4.88231C10.7763 4.76061 10.6203 4.69602 10.4608 4.70858C10.2769 4.72305 10.0627 4.93724 9.63432 5.36561Z" stroke="#18191a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>Tap to unmute</span>
                `,
            });

            tapToUnmuteButton.addEventListener('click', () => handleTapToUnmute('tap-to-unmute-button'));
            tapToUnmuteButton.addEventListener('touchend', () => handleTapToUnmute('tap-to-unmute-button'));

            customControlsWrap.appendChild(tapToUnmuteButton);
        }
    }

    videojs.registerComponent('CustomSeekAndPlayControls', CustomSeekAndPlayControls);
    videojs.registerComponent('CustomPlaybackRateControls', CustomPlaybackRateControls);
    videojs.registerComponent('CustomVideoTimestampAndChapterTitle', CustomVideoTimestampAndChapterTitle);
    videojs.registerComponent('CustomTapToUnmuteButton', CustomTapToUnmuteButton);
    videojs.registerComponent('CustomControlBarSeekButtons', CustomControlBarSeekButtons);

    bcInstance._player.on('play', () => {
        playPauseButton?.classList.remove('is-paused');
    });

    bcInstance._player.on('pause', () => {
        playPauseButton?.classList.add('is-paused');
    });

    bcInstance._player.on('volumechange', () => {
        bcInstance._handleVolumeChange();

        if (!bcInstance._player.muted() && tapToUnmuteButton.classList.contains('is-visible')) {
            handleTapToUnmute('volume-control');
        }
    });

    bcInstance._player.one('loadedmetadata', () => setTapToUnmute());

    playbackRateMenuButton?.on('touchend', (e) => {
        e.preventDefault();
        playbackRateControlsWrap.classList.toggle('is-expanded');
    });

    playbackRateMenuButton?.on('click', () => {
        playbackRateControlsWrap.classList.toggle('is-expanded');
    });

    bcInstance._player.addChild('CustomSeekAndPlayControls', {});
    bcInstance._player.addChild('CustomPlaybackRateControls', {});
    bcInstance._player.addChild('CustomVideoTimestampAndChapterTitle', {});
    bcInstance._player.addChild('CustomTapToUnmuteButton', {});
    bcInstance._player.addChild('CustomControlBarSeekButtons', {});
};

export default createCustomComponents;
